/**
 * @file Drawer shell for the admin-only "Derived values" entry on an open form
 * (SYD-2404 §11, per-form view). Consumer: `FormWorkflowSelect`, which owns the
 * menu entry, the right it is gated on and this `open` flag.
 * The shell holds `open` and nothing else: everything stateful lives in
 * `FormDerivedValuesPanel`, which `destroyOnHidden` unmounts, so a closed drawer
 * keeps no `formStore` subscription — and the drawer itself is never gated on
 * mount, or antd would lose its close transition.
 * `uiState` owns the width, outliving this component, so it survives that
 * unmounting and persists per user under `panelWidth-formDerivedValues`.
 */
import { FC } from 'react';
import { Drawer, theme } from 'antd';
import { useSnapshot } from 'valtio';

import { drawerDraggerStyle } from '../../atoms/drawerDraggerStyle';
import { FormDerivedValuesPanel } from './FormDerivedValuesPanel';
import {
  panelMaxWidth,
  panelViewportClamp,
  setPanelWidth,
  uiState,
} from '../../../store/uiState';
import { formDeriveCopy } from '@/services/formDerives';

type FormDerivedValuesDrawerProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Per-form derived value diagnostics, in a resizable drawer.
 * @component
 */
export const FormDerivedValuesDrawer: FC<FormDerivedValuesDrawerProps> = ({ open, onClose }) => {
  const { token } = theme.useToken();
  const { width } = useSnapshot(uiState.formDerivedValuesDrawer, { sync: true });

  return (
    <Drawer
      title={formDeriveCopy.tabLabel}
      placement="right"
      size={width}
      maxSize={panelMaxWidth('formDerivedValuesDrawer')}
      resizable={{ onResize: (next) => setPanelWidth('formDerivedValuesDrawer', next) }}
      styles={{
        dragger: drawerDraggerStyle(token),
        wrapper: { maxWidth: panelViewportClamp('formDerivedValuesDrawer') },
      }}
      onClose={onClose}
      open={open}
      destroyOnHidden
    >
      <FormDerivedValuesPanel />
    </Drawer>
  );
};
